import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Eye, Calendar, DollarSign, Clock } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

interface Contrato {
  id: number;
  contract_number: string;
  title: string;
  description: string;
  monthly_value: string;
  status: string;
  start_date: string;
  end_date: string | null;
  due_day: number;
  file_url: string;
  notes: string;
  created_at: string;
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  finished: 'bg-blue-100 text-blue-700',
};

const statusLabels: Record<string, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  cancelled: 'Cancelado',
  finished: 'Finalizado',
};

export default function ClientContratos() {
  const navigate = useNavigate();
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadContratos();
  }, []);

  const loadContratos = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/contratos/index.php');
      
      if (!response.ok) {
        throw new Error('Erro ao carregar contratos');
      }

      const data = await response.json();
      setContratos(data.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar contratos:', err);
      setError(err.message || 'Erro ao carregar contratos');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (contrato: Contrato) => {
    try {
      const response = await authenticatedFetch(`/api/uploads/serve.php?file=${encodeURIComponent(contrato.file_url)}`);
      
      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contrato_${contrato.contract_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Erro ao baixar contrato:', err);
      alert('Erro ao baixar contrato');
    }
  };

  const handleView = async (contrato: Contrato) => {
    try {
      const response = await authenticatedFetch(`/api/uploads/serve.php?file=${encodeURIComponent(contrato.file_url)}`);
      
      if (!response.ok) {
        throw new Error('Erro ao visualizar arquivo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error('Erro ao visualizar contrato:', err);
      alert('Erro ao visualizar contrato');
    }
  };

  const formatCurrency = (value: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(parseFloat(value));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/cliente/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar ao Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Contratos</h1>
          <p className="mt-2 text-gray-600">
            Visualize e faça download dos seus contratos
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Lista de Contratos */}
        {contratos.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum contrato encontrado
            </h3>
            <p className="text-gray-600">
              Você ainda não possui contratos disponíveis.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {contratos.map((contrato) => (
              <div
                key={contrato.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6"
              >
                {/* Header do Card */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="bg-indigo-100 rounded-lg p-2 mr-3">
                      <FileText className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {contrato.contract_number ? `#${contrato.contract_number}` : 'Contrato'}
                      </h3>
                      <span
                        className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                          statusColors[contrato.status] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {statusLabels[contrato.status] || contrato.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Título */}
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  {contrato.title}
                </h4>

                {/* Descrição */}
                {contrato.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {contrato.description}
                  </p>
                )}

                {/* Informações */}
                <div className="space-y-2 mb-4">
                  {contrato.monthly_value && (
                    <div className="flex items-center text-sm text-gray-600">
                      <DollarSign className="w-4 h-4 mr-2" />
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(contrato.monthly_value)}/mês
                      </span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>Início: {formatDate(contrato.start_date)}</span>
                  </div>
                  {contrato.end_date && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>Fim: {formatDate(contrato.end_date)}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>Vencimento: Dia {contrato.due_day}</span>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex gap-2 pt-4 border-t">
                  <button
                    onClick={() => handleView(contrato)}
                    className="flex-1 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Visualizar
                  </button>
                  <button
                    onClick={() => handleDownload(contrato)}
                    className="flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
